import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Tag, Trash2 } from "react-feather";
import toast from "react-hot-toast";
import { useGetStockInQuery, useLazyGetStockQrCodeQuery, useDeleteStockInMutation } from "../../stockApi";
import { useCanPrintStockIn, useCanDeleteStockIn } from "../../useStockPermissions";
import ReasonPrompt from "../../../../components/common/ReasonPrompt";
import ActorName from "../../../../components/common/ActorName";
import StockDocumentPrintModal from "../../components/print/StockDocumentPrintModal";
import { composeQrImage, printComposedImage } from "../../../masters/customers/qrImageUtils";
import BackButton from "../../../../components/common/BackButton";
import { useGetCompanyInfoQuery } from "../../../settings/settingsApi";
import StockVatTotals, { vatRateLabel } from "../../components/StockVatTotals";

const money = (v) => (v == null ? "-" : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// A per-line "Print Barcode Label" - the API generates one BarcodeValue per
// received batch/line (PRD:{branchId}:{productCode}:{stockInId}:{lineNumber},
// not per-product), matching how the handheld scanning device work (a later
// phase) will actually read stock back in. Reuses the exact same QR canvas-
// compose + popup-print utility already built for Customer/Location QR
// codes (qrImageUtils.js) rather than a new one.
const BarcodeLabelButton = ({ branchId, line }) => {
  const [triggerQr, { isFetching }] = useLazyGetStockQrCodeQuery();

  const handlePrint = async () => {
    try {
      const { data: blob } = await triggerQr({ branchId, productCode: line.productCode, customBarcodeValue: line.barcodeValue });
      if (!blob) return toast.error("Could not generate the barcode label");
      const url = await composeQrImage(blob, { title: line.productName, subtitle: line.barcodeValue });
      printComposedImage(url, line.productName);
    } catch {
      toast.error("Could not generate the barcode label");
    }
  };

  return (
    <button type="button" className="btn btn-link p-1" title="Print Barcode Label" onClick={handlePrint} disabled={isFetching}>
      <Tag size={14} />
    </button>
  );
};

const StockInDetail = () => {
  const { recordGuid } = useParams();
  const canPrint = useCanPrintStockIn();
  const canDelete = useCanDeleteStockIn();
  const { data: stockIn, isLoading, isError, error } = useGetStockInQuery(recordGuid);
  const { data: companyInfo } = useGetCompanyInfoQuery(stockIn?.branchId, { skip: !stockIn });
  const [deleteStockIn] = useDeleteStockInMutation();
  const [showPrint, setShowPrint] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);

  const handleDelete = async (deleteReason) => {
    try {
      await deleteStockIn({ recordGuid, deleteReason: deleteReason || null }).unwrap();
      toast.success("Stock-In deleted - stock balance reversed");
      setShowDeleteForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not delete the Stock-In");
    }
  };

  if (isLoading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (isError || !stockIn) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <BackButton className="d-inline-flex align-items-center mb-3" />
          <div className="alert alert-danger">Could not load this Stock-In ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  const printProps = {
    title: "GOODS RECEIVED NOTE",
    docNo: stockIn.stockInNo,
    docDate: new Date(stockIn.stockInDate).toLocaleDateString(),
    branchLabel: stockIn.branchCode,
    partyLabel: "Supplier",
    partyName: stockIn.supplierName,
    // Resolved server-side now (Batch44 - Stock-In carries a real SupplierId+
    // SupplierBranchId pair for both LPO-linked and direct postings).
    partyAddress: stockIn.supplierAddress || "",
    partyMobile: stockIn.supplierMobile || stockIn.supplierTelephone || "",
    remarks: stockIn.remarks,
    // VAT per line and exact totals, as stored by the API (Batch172).
    showVatDetail: true,
    vatPercent: vatRateLabel(stockIn.lines),
    lines: stockIn.lines.map((l) => ({
      qty: l.receivedQty,
      productName: l.productName,
      price: l.unitRate,
      amount: l.lineAmount,
      vatPercent: l.vatPercent,
      vatAmount: l.vatAmount,
      lineTotal: l.lineTotal,
    })),
    totals: { subTotal: stockIn.totalAmount, vatAmount: stockIn.vatAmount, netAmount: stockIn.grandTotal },
    preparedByName: stockIn.createdByName,
    ...(companyInfo && {
      companyName: companyInfo["Company.Name"] || undefined,
      companyAddress: companyInfo["Company.Address"] || undefined,
      companyPhone: companyInfo["Company.Phone"] || undefined,
      companyEmail: companyInfo["Company.Email"] || undefined,
    }),
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <BackButton />
              <h4>{stockIn.stockInNo}</h4>
              <h6>{stockIn.branchCode}{stockIn.lpoNumber ? ` - Against LPO ${stockIn.lpoNumber}` : " - Direct / Ad-hoc"}</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2">
            {canPrint && (
              <button type="button" className="btn btn-outline-primary d-inline-flex align-items-center gap-1" onClick={() => setShowPrint(true)}>
                <Printer size={16} /> Print
              </button>
            )}
            {canDelete && !stockIn.isDeleted && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowDeleteForm((s) => !s)}>
                <Trash2 size={16} /> Delete
              </button>
            )}
          </div>
        </div>

        {showDeleteForm && (
          <ReasonPrompt
            label="Delete Reason (optional) - this reverses the stock balance this Stock-In added"
            confirmLabel="Delete Stock-In"
            required={false}
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteForm(false)}
          />
        )}

        {stockIn.isDeleted && (
          <div className="alert alert-danger">
            Deleted by {stockIn.deletedByName} on {new Date(stockIn.deletedDate).toLocaleString()}
            {stockIn.deleteReason ? `: ${stockIn.deleteReason}` : ""}
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-4"><strong>Supplier:</strong> {stockIn.supplierName}</div>
              <div className="col-md-3"><strong>Invoice Ref:</strong> {stockIn.invoiceRef || "-"}</div>
              <div className="col-md-2"><strong>Invoice Date:</strong> {stockIn.invoiceDate ? new Date(stockIn.invoiceDate).toLocaleDateString() : "-"}</div>
              <div className="col-md-3"><strong>Date:</strong> {new Date(stockIn.stockInDate).toLocaleDateString()}</div>
              {stockIn.remarks && <div className="col-md-12 mt-2"><strong>Remarks:</strong> {stockIn.remarks}</div>}
            </div>

            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>#</th><th>Product</th><th>Received Qty</th><th>Unit</th><th className="text-end">Rate</th>
                    <th className="text-end">Amount (ex VAT)</th><th className="text-end">VAT %</th><th className="text-end">VAT</th>
                    <th className="text-end">Total (inc VAT)</th><th>Barcode</th>
                  </tr>
                </thead>
                <tbody>
                  {stockIn.lines.map((l, idx) => (
                    <tr key={l.lineId}>
                      <td>{idx + 1}</td>
                      <td>{l.productName}</td>
                      <td>{l.receivedQty}</td>
                      <td>{l.unit}</td>
                      <td className="text-end">{money(l.unitRate)}</td>
                      <td className="text-end">{money(l.lineAmount)}</td>
                      <td className="text-end">{Number(l.vatPercent)}%</td>
                      <td className="text-end">{money(l.vatAmount)}</td>
                      <td className="text-end fw-semibold">{money(l.lineTotal)}</td>
                      <td><BarcodeLabelButton branchId={stockIn.branchId} line={l} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end mt-2">
              <StockVatTotals subTotal={stockIn.totalAmount} vatAmount={stockIn.vatAmount} grandTotal={stockIn.grandTotal} vatRate={vatRateLabel(stockIn.lines)} />
            </div>

            <div className="text-muted small mt-3">
              Created by <ActorName action="Created" name={stockIn.createdByName} /> on {new Date(stockIn.createdDate).toLocaleString()}
            </div>
          </div>
        </div>

        <StockDocumentPrintModal show={showPrint} onClose={() => setShowPrint(false)} docProps={printProps} copies={1} />
      </div>
    </div>
  );
};

export default StockInDetail;
